import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { AttachMoneyOutlined } from '@mui/icons-material';
import { Link, useNavigate, useParams } from 'react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import PageContainer from '../layout/PageContainer';
import { Alert, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import type { Customer } from '../../models/Customer';
import { GET_CUSTOMER_BY_ID_QUERY, GET_PRODUCT_HISTORICAL_BULK_PRICING_MARKUP_LINE_ITEMS_BY_CUSTOMER_ID_QUERY } from '../../queries/CustomerQueries';
import type { Product } from '../../models/Product';
import { toUSCurrency } from '../../utilities/CurrencyUtilities';
import { GET_TOTAL_ACTIVE_ANNUAL_OVERHEAD_COST } from '../../queries/OverheadCostLineItemQueries';
import { GET_TOTAL_CONSUMABLE_COST } from '../../queries/ConsumableLineItemQueries';
import { ProductHistoricalBulkPricingMarkupLineItem } from '../../models/ProductBulkPricingMarkupLineItem';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { arraysToMap } from '../../utilities/MapUtilities';

export default function CustomerBulkCosting() {
  const { customerId } = useParams();
  const navigate = useNavigate();

  const notifications = useNotifications();

  const [getCustomerById] = useLazyQuery(
    GET_CUSTOMER_BY_ID_QUERY);

  const [getProductHistoricalBulkPricingMarkupLineItemsByCustomerId] = useLazyQuery(
    GET_PRODUCT_HISTORICAL_BULK_PRICING_MARKUP_LINE_ITEMS_BY_CUSTOMER_ID_QUERY);

  const { data: totalActiveAnnualOverheadCostData } = useQuery(
    GET_TOTAL_ACTIVE_ANNUAL_OVERHEAD_COST);

  const { data: totalConsumableCostData } = useQuery(
    GET_TOTAL_CONSUMABLE_COST);

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [historicalBulkPricingMarkupLineItems, setHistoricalBulkPricingMarkupLineItems] = useState<Map<number, Map<string, ProductHistoricalBulkPricingMarkupLineItem[]>>>(new Map());
  const [selectedHistoricalBulkPricingDate, setSelectedHistoricalBulkPricingDate] = useState<Map<number, string>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const totalOverheadCostPerPound = useMemo(() => {
    return (totalActiveAnnualOverheadCostData?.totalActiveAnnualOverheadCost || 0.00) / 2000;
  }, [totalActiveAnnualOverheadCostData]);

  const totalLaborCostPerPound = useCallback((amount: number) => {
    return ((amount / 14) * 40.0) / amount;
  }, []);

  const totalConsumableCostPerPound = useCallback((amount: number) => {
    return (totalConsumableCostData?.totalConsumableCost || 0.00) / amount;
  }, [totalConsumableCostData?.totalConsumableCost]);

  const totalNonIngredientCostPerPound = useCallback((amount: number) => {
    return totalOverheadCostPerPound + totalLaborCostPerPound(amount) + totalConsumableCostPerPound(amount);
  }, [totalOverheadCostPerPound, totalLaborCostPerPound, totalConsumableCostPerPound]);

  const totalCostPerPound = useCallback((totalIngredientCostPerPound: number, amount: number) => {
    return totalIngredientCostPerPound + totalNonIngredientCostPerPound(amount) + totalOverheadCostPerPound + totalLaborCostPerPound(amount) + totalConsumableCostPerPound(amount);
  }, [totalNonIngredientCostPerPound, totalOverheadCostPerPound, totalLaborCostPerPound, totalConsumableCostPerPound]);

  const loadData = useCallback(async () => {
    setError(null);

    const id = +(customerId || '0');

    if (id > 0) {
      setIsLoading(true);

      try {
        const { data, error: getCustomerByIdError } = await getCustomerById(
          { variables: { id } }
        );

        const { data: historicalBulkPricingMarkupLineItemsData, error: getProductHistoricalBulkPricingMarkupLineItemsByCustomerIdError } = await getProductHistoricalBulkPricingMarkupLineItemsByCustomerId(
          { variables: { id } }
        );

        setIsLoading(false);
        setError(null);

        if (getCustomerByIdError) {
          setError(getCustomerByIdError);
        }

        if (getProductHistoricalBulkPricingMarkupLineItemsByCustomerIdError) {
          notifications.show(
            'Failed to get historical bulk pricing markup line items. Reason: Unknown.',
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }

        if (data?.customer) {
          setCustomer(data?.customer);
        } else {
          setError({ message: 'No customer found' } as Error);
        }

        if (historicalBulkPricingMarkupLineItemsData?.historicalBulkPricingMarkupLineItemsByProduct) {
          const historicalBulkPricingMarkupLineItemsByProductId = arraysToMap<number, string, ProductHistoricalBulkPricingMarkupLineItem[]>(
            historicalBulkPricingMarkupLineItemsData.historicalBulkPricingMarkupLineItemsByProduct);

          setHistoricalBulkPricingMarkupLineItems(
            historicalBulkPricingMarkupLineItemsByProductId);

          const initialSelectedHistoricalBulkPricingDate = new Map<number, string>();

          historicalBulkPricingMarkupLineItemsByProductId.keys().forEach((productId) => {
            const historicalBulkPricingMarkupLineItemsByDate = historicalBulkPricingMarkupLineItemsByProductId.get(
              productId);

            if (historicalBulkPricingMarkupLineItemsByDate &&
              historicalBulkPricingMarkupLineItemsByDate.size > 0) {

              initialSelectedHistoricalBulkPricingDate.set(
                productId,
                [...historicalBulkPricingMarkupLineItemsByDate.keys()][0]);
            }
          });

          setSelectedHistoricalBulkPricingDate(
            initialSelectedHistoricalBulkPricingDate);
        }

      } catch (showDataError) {
        setError(showDataError as Error);
      }
    }
    setIsLoading(false);
  }, [customerId, getCustomerById, getProductHistoricalBulkPricingMarkupLineItemsByCustomerId, notifications]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSelectFieldChange = useCallback(
    (productId: number, date: string) => {
      const updatedSelectedHistoricalBulkPricingDate = new Map<number, string>(
        selectedHistoricalBulkPricingDate);

      updatedSelectedHistoricalBulkPricingDate.set(
        productId,
        date);

      setSelectedHistoricalBulkPricingDate(
        updatedSelectedHistoricalBulkPricingDate);
    },
    [selectedHistoricalBulkPricingDate],
  );

  const handleBack = useCallback(() => {
    navigate(`/dashboard/customers/${customerId}`);
  }, [customerId, navigate]);

  if (isLoading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          m: 1,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Alert severity="error">{error.message}</Alert>
      </Box>
    );
  }

  const pageTitle = `${customer?.name} Bulk Costing`;

  return (
    <PageContainer
      title={pageTitle}
      breadcrumbs={[
        { title: 'Customers', path: '/dashboard/customers' },
        { title: `${customer?.name}`, path: `/dashboard/customers/${customer?.id}` },
        { title: 'Bulk Costing' }
      ]}
    >
      <Box
        sx={{ width: '100%' }}
      >
        <Stack spacing={2} justifyContent="space-between" direction="row" sx={{ width: '100%' }}>
          <Box sx={{ width: '100%' }}>
            <TableContainer sx={{ maxHeight: '550px' }}>
              <Table sx={{ minWidth: 550 }} aria-label="table" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{
                      paddingLeft: 0,
                      width: '6%'
                    }} component="th" scope="row">Name</TableCell>
                    <TableCell sx={{ paddingLeft: 0, width: '48%' }} component="th" scope="row">Pricing</TableCell>
                    <TableCell sx={{ paddingLeft: 0, width: '24%' }} component="th" scope="row">
                      Historical Pricing
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(customer?.bulkPricingProducts || []).map((product: Product) => (
                    <TableRow
                      key={product.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell sx={{
                        paddingLeft: 0,
                        width: '6%'
                      }}>
                        <a href={`/dashboard/products/${product?.id}`}>{product?.name}</a>
                      </TableCell>
                      <TableCell sx={{ paddingLeft: 0, width: '48%' }}>
                        <TableContainer>
                          <Table aria-label="dense table" size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontSize: '90%', paddingLeft: 0, width: '10%' }} component="th" scope="row">Pounds</TableCell>
                                <TableCell sx={{ fontSize: '90%', paddingLeft: 0, width: '20%' }} component="th" scope="row">Total Ing. Cost</TableCell>
                                <TableCell sx={{ fontSize: '90%', paddingLeft: 0, width: '25%' }} component="th" scope="row">Total Non-ing. Cost</TableCell>
                                <TableCell sx={{ fontSize: '90%', paddingLeft: 0, width: '15%' }} component="th" scope="row">Total Cost</TableCell>
                                <TableCell sx={{ fontSize: '90%', paddingLeft: 0, width: '10%' }} component="th" scope="row">Markup</TableCell>
                                <TableCell sx={{ fontSize: '90%', paddingLeft: 0, width: '20%' }} component="th" scope="row">Total Markup $</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {product.bulkPricingMarkupLineItems?.map((markupLineItem) => (
                                <TableRow key={markupLineItem.id}>
                                  <TableCell sx={{ fontSize: '90%', paddingLeft: 0, width: '10%' }}>
                                    {markupLineItem.pounds}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '90%', paddingLeft: 0, width: '20%' }}>
                                    ${toUSCurrency(product?.totalAverageItemCostPerBatchPound)} /lb
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '90%', paddingLeft: 0, width: '25%' }}>
                                    ${toUSCurrency(totalNonIngredientCostPerPound(markupLineItem.pounds))} /lb
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '90%', paddingLeft: 0, width: '15%' }}>
                                    ${toUSCurrency(totalCostPerPound(product.totalAverageItemCostPerBatchPound || 0, markupLineItem.pounds))} /lb
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '90%', paddingLeft: 0, width: '10%' }}>
                                    {markupLineItem.markup}%
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '90%', paddingLeft: 0, width: '20%' }}>
                                    ${toUSCurrency(totalCostPerPound(product.totalAverageItemCostPerBatchPound || 0, markupLineItem.pounds) * (markupLineItem.markup / 100))} /lb
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </TableCell>
                      <TableCell sx={{ paddingLeft: 0, width: '24%' }}>
                        {historicalBulkPricingMarkupLineItems.has(product.id) ?
                          <TableContainer>
                            <Table aria-label="dense table" size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontSize: '90%', paddingLeft: 0, width: '50%' }} component="th" scope="row">
                                    <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                                      <Box>
                                        Markup
                                      </Box>
                                      {historicalBulkPricingMarkupLineItems.has(product.id) ?
                                        <TextField
                                          select
                                          size="small"
                                          name="date"
                                          value={selectedHistoricalBulkPricingDate.get(product.id) || ''}
                                          onChange={(event) => handleSelectFieldChange(product.id, event.target.value)}
                                          slotProps={{
                                            input: {
                                              sx: { fontSize: '95%' }
                                            },
                                            select: {
                                              inputProps: {
                                                sx: { pt: '3px', pb: '1px' }
                                              }
                                            }
                                          }}>
                                          {[...historicalBulkPricingMarkupLineItems.get(product.id)?.keys() || []].map((date) => (
                                            <MenuItem key={date} value={date}>{date.substring(4)}/{date.substring(0, 4)}</MenuItem>
                                          ))}
                                        </TextField> : <Box></Box>
                                      }
                                    </Stack>
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '90%', paddingLeft: 0, width: '50%' }} component="th" scope="row">
                                    Total Markup $
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              {historicalBulkPricingMarkupLineItems.get(product.id)?.get(selectedHistoricalBulkPricingDate.get(product.id) || '')?.map(b => (
                                <TableBody key={b.id}>
                                  <TableRow>
                                    <TableCell sx={{ fontSize: '90%', paddingLeft: 0, width: '50%' }}>
                                      {b.markup}%
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '90%', paddingLeft: 0, width: '50%' }}>
                                      ${toUSCurrency(totalCostPerPound(product.totalAverageItemCostPerBatchPound || 0, b.pounds) * (b.markup / 100))} /lb
                                    </TableCell>
                                  </TableRow>
                                </TableBody>
                              ))}
                            </Table>
                          </TableContainer> : <Box>N/A</Box>
                        }
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Stack>
        <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            variant="contained"
            startIcon={<AttachMoneyOutlined />}
            component={Link}
            to={`/dashboard/customers/${customer?.id}/bulk-costing-price-list`}
          >
            Final Price List
          </Button>
        </Stack>
      </Box >
    </PageContainer >
  );
}
